import { msAPI } from "./index.js";

// ==========================================================================
// POS / retailer API credentials (menu-service)
//
// The provider catalogue is served by menu-service rather than hardcoded, so a
// provider added server-side appears here without a dashboard release. Ported
// from stockroom's SellerAPIDialog, which is the tool these endpoints were
// built for.
//
// The descriptor each provider returns doubles as the credential form's
// template: its own keys are the fields, and the type of each value decides the
// input. See `providerCatalog.js` for the metadata keys that are filtered out.
// ==========================================================================

/**
 * The providers this environment supports.
 *
 * @returns {Promise<Array<{
 *   provider: string,
 *   required_fields?: string[],
 *   provide_oauth_link?: boolean,
 *   instructions?: string,
 *   [field: string]: unknown
 * }>>}
 */
export const getSupportedPOSProviders = async () => {
  return (await msAPI.get("admin/credentials/types")).data;
};

/**
 * Attach credentials to a store that has none.
 *
 * @param {string} storeId
 * @param {string} provider provider key from the catalogue
 * @param {object} fields the filled-in descriptor
 */
export const createPOSCredentials = async (storeId, provider, fields) => {
  return (await msAPI.post(`admin/credentials/add/${encodeURIComponent(storeId)}`, {
    provider,
    fields,
  })).data;
};

/** Replace the credentials on a store that already has some. */
export const updatePOSCredentials = async (storeId, provider, fields) => {
  return (await msAPI.patch(`admin/credentials/update/${encodeURIComponent(storeId)}`, {
    provider,
    fields,
  })).data;
};

/** Detach a store's credentials entirely. */
export const deletePOSCredentials = async (storeId) => {
  return (await msAPI.delete(`admin/credentials/remove/${encodeURIComponent(storeId)}`)).data;
};

/**
 * Dry-run a set of credentials against the provider without saving them.
 *
 * Note the asymmetry with create/update: this one takes the fields flat rather
 * than in a `{provider, fields}` envelope — but it still needs `provider`, as a
 * sibling of the fields, to know which integration to dial. Omitting it is a
 * 400. (stockroom got this for free by posting a clone of the whole provider
 * descriptor, which carries `provider` among the field values.)
 */
export const testPOSCredentials = async (storeId, provider, fields) => {
  return (await msAPI.post(`admin/credentials/test/${encodeURIComponent(storeId)}`, {
    ...fields,
    provider,
  })).data;
};

/**
 * Shops available for a Lightspeed Retail account, once its credentials are
 * entered but before they are saved — the account has to be resolved to a single
 * shop id.
 */
export const getLightspeedShops = async (storeId, credentials) => {
  return (await msAPI.get(
    `admin/credentials/lightspeed-retail/shops/${encodeURIComponent(storeId)}`,
    { params: credentials }
  )).data;
};

/** Master tokens an operator can pick from instead of entering one by hand. */
export const getLightspeedRetailMasterTokens = async () => {
  return (await msAPI.get("admin/credentials/lightspeed-retail/master-tokens")).data;
};
